import { motion } from "motion/react";
import { ChevronDown, FileText, Settings, ArrowUpRight } from "lucide-react";
import { useState, useMemo } from "react";

import { AssetAllocationCard, PromoCard, StatsCard, ReportCard, PillNav, BrandIcon, Icons } from "../components/dashboard-components";
import { useRWAStore } from "../store/rwa-store";

interface PortfolioItem {
    assetId: string;
    amount: number;
    avgCost: number;
    name: string;
    symbol: string;
    currentPrice: number;
    change24h: number;
    value: number;
    color: string;
}

export default function DashboardView() {
    const { currentUser, assets, updatePortfolio, transactions: _transactions } = useRWAStore();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [selectedAssetId, setSelectedAssetId] = useState(assets[0]?.id || "");
    const [amount, setAmount] = useState("");

    const portfolioData = useMemo(() => {
        if (!currentUser || !currentUser.portfolio) return [];
        return currentUser.portfolio.map(item => {
            const asset = assets.find(a => a.id === item.assetId);
            if (!asset) return null;
            return {
                ...item,
                name: asset.name,
                symbol: asset.symbol,
                currentPrice: asset.price,
                change24h: asset.change24h,
                value: item.amount * asset.price,
                color: asset.type === 'crypto' ? '#10b981' : '#3b82f6', // Emerald vs Blue
            };
        }).filter(Boolean) as PortfolioItem[];
    }, [currentUser, assets]);

    const totalValue = portfolioData.reduce((acc, curr) => acc + curr.value, 0);

    // --- METRICS CALCULATION ---
    const cryptoValue = portfolioData.filter(i => assets.find(a => a.id === i.assetId)?.type === 'crypto').reduce((acc, curr) => acc + curr.value, 0);
    const rwaValue = portfolioData.filter(i => assets.find(a => a.id === i.assetId)?.type === 'rwa').reduce((acc, curr) => acc + curr.value, 0);

    const handleAddAsset = (e: React.FormEvent) => {
        e.preventDefault();
        const targetAsset = assets.find(a => a.id === selectedAssetId);
        const price = targetAsset ? targetAsset.price : 0;
        updatePortfolio(selectedAssetId, parseFloat(amount), price);
        setIsAddModalOpen(false);
        setAmount("");
    };

    if (!currentUser) return null;

    // Earnings from Mock History
    const history = currentUser.portfolioHistory || [];
    const yesterdayValue = history.length > 1 ? history[history.length - 2].value : (totalValue * 0.9);
    const todayEarnings = totalValue - yesterdayValue;
    const earningsPercentage = yesterdayValue > 0 ? ((todayEarnings / yesterdayValue) * 100).toFixed(1) : "0.0";

    return (
        <div className="w-full max-w-6xl mx-auto p-6">
            {/* HEADER AREA */}
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
                <div>
                    <h1 className="text-5xl font-bold text-white mb-2 tracking-tight">Welcome Back, {currentUser.name}!</h1>
                    <p className="text-stone-400">Current summary of your crypto portfolio</p>
                </div>

                <div className="flex gap-4">
                    {/* Action Buttons from image */}
                    <button className="flex items-center gap-2 bg-[#1c1c1c] text-white px-5 py-2.5 rounded-full border border-white/5 hover:bg-[#252525] transition-colors">
                        Activity <ChevronDown className="w-4 h-4" />
                    </button>
                    <button className="flex items-center gap-2 bg-[#1c1c1c] text-white px-5 py-2.5 rounded-full border border-white/5 hover:bg-[#252525] transition-colors">
                        Journal <FileText className="w-4 h-4" />
                    </button>
                    <button className="flex items-center gap-2 bg-[#1c1c1c] text-white px-5 py-2.5 rounded-full border border-white/5 hover:bg-[#252525] transition-colors">
                        Filter
                    </button>
                    <button className="w-10 h-10 flex items-center justify-center bg-[#1c1c1c] text-white rounded-full border border-white/5 hover:bg-[#252525] transition-colors">
                        <Settings className="w-4 h-4" />
                    </button>
                </div>
            </header>

            <div className="mb-10">
                <PillNav />
            </div>

            {/* DASHBOARD GRID */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
                {/* 1. Asset Allocation (4 cols) */}
                <div className="md:col-span-4 h-72">
                    <AssetAllocationCard totalValue={totalValue} cryptoValue={cryptoValue} rwaValue={rwaValue} />
                </div>

                {/* 2. Crypto Mastery Promo (5 cols) */}
                <div className="md:col-span-5 h-72">
                    <PromoCard />
                </div>

                {/* 3. Small Cards Column (3 cols) */}
                <div className="md:col-span-3 flex flex-col gap-6 h-72">
                    <div className="flex-1">
                        <StatsCard earnings={todayEarnings} percentage={parseFloat(earningsPercentage)} />
                    </div>
                    <div className="flex-1">
                        <ReportCard />
                    </div>
                </div>
            </div>

            {/* BOTTOM SECTION: HOLDINGS & EARNINGS BREAKDOWN */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Earnings Breakdown Placeholder */}
                <div className="bg-[#111111] rounded-4xl border border-white/5 p-8 min-h-[300px]">
                    <div className="flex justify-between items-center mb-8">
                        <h3 className="text-xl font-medium text-stone-200">Earnings Breakdown</h3>
                        <button className="flex items-center gap-1 bg-[#1c1c1c] px-3 py-1.5 rounded-full text-xs font-medium text-stone-400">
                            Sort by Month <ChevronDown className="w-3 h-3" />
                        </button>
                    </div>
                    {/* Placeholder Graph using CSS only for speed */}
                    {/* Dynamic Bar Graph */}
                    <div className="h-40 flex items-end gap-2 px-4">
                        {history.length > 0 ? history.map((day, i) => {
                            // Normalize for height (max 100%)
                            const maxVal = Math.max(...history.map(d => d.value));
                            const height = (day.value / maxVal) * 100;
                            const isToday = i === history.length - 1;

                            return (
                                <div key={i} className="flex-1 bg-stone-800/20 rounded-t-lg relative group tooltip-container">
                                    <div
                                        className={`absolute bottom-0 left-0 right-0 transition-all duration-500 ${isToday ? 'bg-[#bef264]' : 'bg-emerald-500/30'}`}
                                        style={{ height: `${height}%` }}
                                    />
                                    {/* Simple Tooltip on Hover */}
                                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-neutral-800 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                        ${(day.value / 1000).toFixed(1)}k
                                    </div>
                                </div>
                            )
                        }) : (
                            <div className="w-full h-full flex items-center justify-center text-stone-600 text-sm">No History Data</div>
                        )}
                    </div>
                    <div className="flex justify-between mt-4 text-xs text-stone-600 font-mono uppercase">
                        <span>M</span><span>T</span><span>W</span><span>T</span><span>F</span><span>S</span><span>S</span>
                    </div>
                </div>

                {/* Crypto Holdings List */}
                <div className="bg-[#111111] rounded-4xl border border-white/5 p-8 min-h-[300px]">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-medium text-stone-200">Crypto Holdings</h3>
                        <button className="w-10 h-10 bg-[#bef264] rounded-full flex items-center justify-center text-black hover:bg-[#a3e635]">
                            <ArrowUpRight className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="space-y-2">
                        {portfolioData.map((asset, i) => (
                            <div key={i} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/5 transition-colors group cursor-pointer">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white">
                                        {asset.symbol === 'BTC' ? <BrandIcon icon={Icons.BTC} className="w-5 h-5" /> :
                                            asset.symbol === 'ETH' ? <BrandIcon icon={Icons.ETH} className="w-5 h-5" /> :
                                                <BrandIcon icon={Icons.LTC} className="w-5 h-5" />}
                                    </div>
                                    <div>
                                        <div className="font-medium text-white">{asset.name}</div>
                                        <div className="text-xs text-stone-500">{asset.amount} {asset.symbol}</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-mono text-white">${asset.value.toLocaleString()}</div>
                                    <div className={`text-xs ${asset.change24h >= 0 ? 'text-[#bef264]' : 'text-red-500'}`}>
                                        {asset.change24h > 0 ? '+' : ''}{asset.change24h}%
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ADD ASSET MODAL */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-xs">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-neutral-900 border border-neutral-800 p-8 rounded-2xl w-full max-w-md"
                    >
                        <h2 className="text-xl font-bold mb-6">Add Position</h2>
                        <form onSubmit={handleAddAsset} className="space-y-4">
                            <div>
                                <label className="block text-sm text-neutral-400 mb-2">Select Asset</label>
                                <select
                                    value={selectedAssetId}
                                    onChange={(e) => setSelectedAssetId(e.target.value)}
                                    className="w-full bg-neutral-950 border border-neutral-800 rounded-lg p-3 text-white focus:outline-hidden focus:border-emerald-500"
                                >
                                    {assets.map(a => (
                                        <option key={a.id} value={a.id}>{a.name} ({a.symbol})</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm text-neutral-400 mb-2">Amount</label>
                                <input
                                    type="number"
                                    step="any"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    className="w-full bg-neutral-950 border border-neutral-800 rounded-lg p-3 text-white focus:outline-hidden focus:border-emerald-500"
                                    placeholder="0.00"
                                />
                            </div>
                            <div className="flex gap-4 pt-4">
                                <button type="button" onClick={() => setIsAddModalOpen(false)} className="flex-1 py-3 bg-neutral-800 rounded-lg hover:bg-neutral-700">Cancel</button>
                                <button type="submit" className="flex-1 py-3 bg-emerald-500 text-black font-bold rounded-lg hover:bg-emerald-400">Confirm Order</button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
